import { useCallback, useEffect, useMemo, useState } from "react";
import APIRequest, { APIError } from "system/APIRequest";

// This hook is used to fetch data from the API and return it to the caller 
// based on a supplied endpoint. Dependencies can be passed in to trigger a
// refresh of the data when they change. The data is returned as a tuple
export default function useFetchData(endpoint: string | undefined | boolean | null, deps: any[], transformCallback?: any) {
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [data, setData] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<APIError | null>(null);

  const memoizedData = useMemo(() => data, [data]);

  const fetchData = useCallback(() => {
    if (endpoint) {
      setIsLoading(true);
      setError(null);

      APIRequest(endpoint)
        .then((response) => {
          if (transformCallback) {
            response = transformCallback(response);
          }
          setData(response || {});
          setIsLoading(false);
        })
        .catch((err: APIError) => {
          if(err.code === 401) {
            // If we get an unauthorized response (HTTP 401, meaning auth needed, but not provided), bubble it up
            throw err;
          }
          setError(err);
          setIsLoading(false);
        });
    }
  // eslint-disable-next-line
  }, [endpoint]);

  // Fetch data on mount, or when any of the passed in dependencies change, 
  // or when the refresh key changes (allowing the hook to be manually refreshed)
  useEffect(() => {
    fetchData();
  // React complains about the spread operator here because it can't statically check it,
  // but it's necessary for the hook to work properly with the passed in dependencies
  // eslint-disable-next-line
  }, [...deps, fetchData, refreshKey]);

  // This function is returned and can be called to manually refresh the data 
  // without any of the dependencies changing
  const handleRefresh = () => {
    setRefreshKey(refreshKey + 1);
  };

  return [memoizedData, isLoading, error, handleRefresh];
}